import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { Shield, Loader2, AlertTriangle } from 'lucide-react';

export default function ForcePasswordChange() {
  const { user, mustChangePassword, clearMustChangePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword) {
      setError('Please enter your temporary password');
      return;
    }
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from your temporary password');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully! Welcome to HR Performance Hub.');
      clearMustChangePassword();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (!mustChangePassword) {
    return null;
  }

  return (
    <Dialog open={mustChangePassword} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Change Your Password
          </DialogTitle>
          <DialogDescription>
            Welcome, {user?.full_name}! For security reasons, you must change your temporary password before continuing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="temp-password">Temporary Password</Label>
            <Input
              id="temp-password"
              type="password"
              placeholder="Enter the password from your invite email"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              data-testid="force-temp-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="force-new-password">New Password</Label>
            <Input
              id="force-new-password"
              type="password"
              placeholder="Create a strong password (min 8 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              data-testid="force-new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="force-confirm-password">Confirm New Password</Label>
            <Input
              id="force-confirm-password"
              type="password"
              placeholder="Re-enter your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              data-testid="force-confirm-password-input"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              type="submit" 
              className="flex-1"
              disabled={loading}
              data-testid="force-change-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password & Continue'
              )}
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={handleLogout}
              disabled={loading}
              data-testid="force-logout-btn"
            >
              Logout
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
